import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/loading';
import ModalErrorMessage from '../../components/modalErrorMessage/modalErrorMessage';
import { useSaveUser } from './hooks/useSaveUser';
import { ASSIGNABLE_ROLES } from './roles';

function UserModal({ open, onClose, onSave, user: selectedUser }) {
  const navigate = useNavigate();
  const { save: saveUserRequest } = useSaveUser();

  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [name, setName] = useState('');
  const [login, setLogin] = useState('');
  const [roles, setRoles] = useState([]);

  const isEditing = !!selectedUser?.id;

  useEffect(() => {
    if (open) {
      setName(selectedUser?.name ?? '');
      setLogin(selectedUser?.login ?? '');
      setRoles(selectedUser?.roles ?? []);
      setShowErrorMessage(false);
      setErrorMessage('');
    }
  }, [selectedUser, open]);

  const toggleRole = (roleKey) => {
    setRoles((prev) =>
      prev.includes(roleKey) ? prev.filter((role) => role !== roleKey) : [...prev, roleKey],
    );
  };

  const saveUser = () => {
    if (!name.trim()) {
      setShowErrorMessage(true);
      setErrorMessage('Inserir um nome é obrigatório.');
      return;
    }
    if (!login.trim()) {
      setShowErrorMessage(true);
      setErrorMessage('Inserir um login é obrigatório.');
      return;
    }
    if (roles.length === 0) {
      setShowErrorMessage(true);
      setErrorMessage('Selecionar ao menos um perfil de acesso é obrigatório.');
      return;
    }

    const input = {
      id: selectedUser?.id,
      name: name.trim(),
      login: login.trim(),
      roles,
    };

    setLoading(true);
    saveUserRequest(input)
      .then((returnFromApi) => {
        setLoading(false);
        onClose();
        onSave(returnFromApi);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(
          String(returnFromApi.response?.data ?? 'Não foi possível salvar o usuário.'),
        );
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.response?.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {isEditing ? 'Editar Usuário' : 'Cadastrar Novo Usuário'}
      </DialogTitle>
      <DialogContent>
        <Loading isToHidden={isLoading === false} />
        {showErrorMessage && (
          <ModalErrorMessage message={errorMessage} setShow={setShowErrorMessage} />
        )}

        <TextField
          id="name"
          label="Nome *"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 2 }}
        />
        <TextField
          id="login"
          label="Login *"
          type="text"
          fullWidth
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          disabled={isEditing}
          helperText={isEditing ? 'O login não pode ser alterado.' : ''}
          sx={{ mt: 2 }}
        />
        {!isEditing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            A senha inicial é <strong>123</strong>. Informe o usuário para alterá-la assim que
            entrar no sistema.
          </Alert>
        )}

        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mt: 3, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Perfis de acesso
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <FormGroup>
          {ASSIGNABLE_ROLES.map((role) => (
            <FormControlLabel
              key={role.key}
              control={
                <Checkbox
                  size="small"
                  checked={roles.includes(role.key)}
                  onChange={() => toggleRole(role.key)}
                />
              }
              label={role.label}
            />
          ))}
        </FormGroup>
        <Box sx={{ height: 8 }} />
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 16px' }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={saveUser} variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    login: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
};

UserModal.defaultProps = {
  user: null,
};

export default UserModal;
